import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate, Link } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useTranslation } from 'react-i18next';
import CookieConsent from "react-cookie-consent";

import PublicReportForm from './PublicReportForm';
import AdminDashboard from './AdminDashboard';
import UserManagement from './UserManagement';
import SignIn from './SignIn';
import PublicAnalytics from './PublicAnalytics';
import HotspotMap from './HotspotMap';
import PrivacyPolicy from './PrivacyPolicy';
import SafetyTips from './SafetyTips'; // Import the new component
import './App.css';

// This hook is now the single source of truth for auth data
export const useAuth = () => {
    const [authData, setAuthData] = useState({
        user: null,
        userRole: null,
        loading: true,
    });

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                const userDocRef = doc(db, "users", firebaseUser.uid);
                const userDoc = await getDoc(userDocRef);
                const role = userDoc.exists() ? userDoc.data().role : 'Researcher'; // Default to most restrictive role
                setAuthData({ user: firebaseUser, userRole: role, loading: false });
            } else {
                setAuthData({ user: null, userRole: null, loading: false });
            }
        });
        return () => unsubscribe();
    }, []);
    return authData;
};

// This component now protects routes based on an array of allowed roles
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { user, userRole, loading } = useAuth();

    if (loading) { 
        return <div className="loading-screen">Loading...</div>; 
    }

    if (!user) { 
        return <Navigate to="/signin" replace />; 
    }

    if (!allowedRoles.includes(userRole)) { 
        // If the user's role is not in the allowed list, redirect them.
        return <Navigate to="/admin" replace />; 
    }

    return children;
};

const SignOutButton = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const auth = getAuth();
    const handleSignOut = async () => {
        try {
            await signOut(auth);
            navigate('/signin');
        } catch (error) {
            console.error("Sign out error", error);
        }
    };
    return <button onClick={handleSignOut} className="nav-link signout-btn">{t('sign_out')}</button>;
};

function App() {
  const { user, userRole, loading } = useAuth();
  const { t, i18n } = useTranslation();
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  const toggleTheme = () => {
      const newTheme = theme === 'light' ? 'dark' : 'light';
      setTheme(newTheme);
  };
  
  const handleLanguageChange = (e) => {
    i18n.changeLanguage(e.target.value);
  };

  useEffect(() => {
      document.body.className = '';
      document.body.classList.add(`${theme}-mode`);
      localStorage.setItem('theme', theme);
  }, [theme]);

  if (loading) {
    return <div className="loading-screen">Loading Application...</div>;
  }

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="logo-container">
          <span className="logo-text">{t('app_logo_text')}</span>
        </div>
        <nav className="main-nav">
          <NavLink to="/" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>{t('public_reporting')}</NavLink>
          <NavLink to="/analytics" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>{t('public_analytics')}</NavLink>
          <NavLink to="/hotspots" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>{t('hotspot_map')}</NavLink>
          {/* --- NEW LINK ADDED --- */}
          <NavLink to="/safety-tips" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>{t('safety_tips_title')}</NavLink>
          {user && (
              <>
                  <NavLink to="/admin" end className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>{t('dashboard')}</NavLink>
                  {userRole === 'Super Admin' && (
                      <NavLink to="/admin/users" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>{t('user_management')}</NavLink>
                  )}
                  <SignOutButton />
              </>
          )}
          <div className="nav-controls">
            <select onChange={handleLanguageChange} value={i18n.language} className="language-switcher-select">
              <option value="en">{t('english')}</option>
              <option value="sw">{t('swahili')}</option>
            </select>
            <button onClick={toggleTheme} className="theme-toggle-btn" title="Toggle theme">
                {theme === 'light' ? '🌙' : '☀️'}
            </button>
          </div>
        </nav>
      </header>

      <main className="app-content">
        <Routes>
          <Route path="/" element={<PublicReportForm />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/analytics" element={<PublicAnalytics theme={theme} />} />
          <Route path="/hotspots" element={<HotspotMap />} /> 
          <Route path="/privacy" element={<PrivacyPolicy />} />
          {/* --- NEW ROUTE ADDED --- */}
          <Route path="/safety-tips" element={<SafetyTips />} />
          
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute allowedRoles={['Researcher', 'Admin', 'Super Admin']}>
                <AdminDashboard theme={theme} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/users" 
            element={
              <ProtectedRoute allowedRoles={['Super Admin']}>
                <UserManagement />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </main>
      
      <CookieConsent
          location="bottom"
          buttonText={t('cookie_consent_accept')}
          cookieName="trardCookieConsent"
          style={{ background: "#2B373B" }}
          buttonStyle={{ color: "#4e503b", fontSize: "13px" }}
          expires={150}
      >
          {t('cookie_consent_message')}{" "}
          <Link to="/privacy" style={{ color: "#a9c6e4", textDecoration: 'underline' }}>
              {t('privacy_policy_nav')}
          </Link>
      </CookieConsent>
    </div>
  );
}

export default App;
